import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { EMPTY_ACTION } from "@/modules/renters/components/constants/paymentConstants";
import { Suspense, useState, useEffect, useCallback } from "react";
import { IContractB2B } from "@/types/contractB2B.type";
import { useParams } from "react-router-dom";
import { getContractsB2B } from "@/common/services/contracts-b2b.api";
import ModalContractB2B from "../modals/ModalCreateContractB2B";

const headers = {
  _id: "ID Contrato",
  valor: "Valor",
  vencimento: "Vencimento",
  vehicles: "Placas",
  active: "Status",
  createdAt: "Criado Em",
};

type ActionItem = {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: (data: any) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disabled?: (data: any) => boolean;
  permission?: string;
};

export default function ContractsTableB2B() {
  const { id: idCompanyB2B } = useParams<{ id: string }>();

  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [contracts, setContracts] = useState<IContractB2B[]>([]);

  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "_id,valor",
    orderBy: "-vencimento",
    itemsPerPage: "10",
  });

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    loadData(filters, page);
    setAction(EMPTY_ACTION);
  };

  const loadData = useCallback(
    async (currentFilters: TableFiltersState, currentPage: number) => {
      if (!idCompanyB2B) return;

      setIsLoading(true);
      setContracts([]);

      const itemsPerPageNumber = Number(currentFilters.itemsPerPage ?? 10);
      const apiFilters = {
        page: currentPage,
        itemsPerPage: itemsPerPageNumber,
        orderBy: currentFilters.orderBy,
        search: currentFilters.search,
      };
      const result = await getContractsB2B(idCompanyB2B, apiFilters);

      setData(() => ({
        itemsPerPage: itemsPerPageNumber,
        totalItems: result.totalItems,
      }));

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message ||
          "Erro ao buscar contratos."
        );
        setIsLoading(false);
        return;
      }

      setContracts(result.data);
      setIsLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idCompanyB2B]
  );

  useEffect(() => {
    if (idCompanyB2B) {
      loadData(filters, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, idCompanyB2B]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const onContractSave = () => {
    loadData(filters, page);
  };



  const actions: ActionItem[] = [
    {
      label: "Editar",
      action: (data: IContractB2B) => {
        setAction({ eventName: "edit", data: data });
      },
    },
  ];

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section p-4 bg-white">
        <TableFilters
          dashboardHeader="Contratos B2B"
          placeholder="Buscar por ID ou Valor do Contrato."
          statusOptions={{}}
          newItemLabel="Novo Contrato"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          entity="vehicles"
          onSearch={handleFilterChange}
          tableData={contracts as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={contracts as unknown as Record<string, unknown>[]}
          actions={actions}
          setFilters={({ orderBy }) => {
            handleFilterChange({ orderBy });
          }}
          renders={{
            active: (activeData) => {
              const active = activeData as boolean;
              return (
                <span
                  className={`font-medium ${active ? "text-green-600" : "text-red-600"
                    }`}
                >
                  {active ? "Ativo" : "Inativo"}
                </span>
              );
            },
            vehicles: (vehiclesData) => {
              const vehicles = vehiclesData as { licensePlate: string }[];
              return vehicles.length;
            },
            vencimento: (dateData) => {
              const date = dateData as Date;
              return new Date(date).toLocaleDateString();
            },
            createdAt: (dateData) => {
              const date = dateData as Date;
              return new Date(date).toLocaleDateString();
            },
            _id: (idData) => {
              const _id = idData as string;
              return (
                <span title={`Contrato ID: ${_id}`}>
                  {_id.substring(0, 8)}...
                </span>
              );
            },
          }}
          entity="vehicles"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
        {isAction("create") && (
          <ModalContractB2B
            isOpen={true}
            onClose={cleanActionData}
            onSave={onContractSave}
            title="Novo Contrato B2B"
          />
        )}

        {isAction("edit") && (
          <ModalContractB2B
            isOpen={true}
            onClose={cleanActionData}
            onSave={onContractSave}
            title="Editar Contrato B2B"
            contractData={action.data as IContractB2B}
          />
        )}
      </div>
    </Suspense>
  );
}
